import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { Heading2, BodyText, BodyLarge, Caption, Container, Flex, Spacer } from './ui';
import AppHeader from './Header';
import { API_BASE_URL } from '../api/tourism';
import { FadeInContainer, FadeInUpContainer } from '../styles/animations';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';

interface AboutPageProps {
  onNavigateTo?: (page: string, data?: any) => void;
}

// ─── Animations ────────────────────────────────────────────────────────────
const drawLine = keyframes`
  from { width: 0; }
  to { width: 80px; }
`;

// ─── Page Container ────────────────────────────────────────────────────────
const PageContainer = styled.div`
  background: ${getColor('neutral.white')};
  min-height: 100vh;
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  overflow-x: hidden;
`;

// ─── Typographic Editorial Hero ────────────────────────────────────────────
const HeroSection = styled.section`
  position: relative;
  padding: 180px ${getSpacing('xl')} 120px;
  text-align: center;
  background: ${getColor('neutral.white')};
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 140px ${getSpacing('lg')} 80px;
  }
  @media (max-width: 480px) {
    padding: 120px ${getSpacing('md')} 60px;
  }
`;

const HeroCompanyName = styled.h1`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: clamp(2.5rem, 7vw, 5.5rem);
  font-weight: 300;
  letter-spacing: 0.12em;
  color: ${getColor('primary.900')};
  margin: 0;
  line-height: 1.1;
  text-transform: uppercase;
`;

const HeroEst = styled.span`
  display: block;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: clamp(0.875rem, 2vw, 1.125rem);
  font-weight: 400;
  letter-spacing: 0.3em;
  color: #CAB96E;
  margin-top: ${getSpacing('lg')};
  text-transform: uppercase;
`;

const HeroSubtitle = styled.p`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: clamp(1rem, 2.5vw, 1.375rem);
  font-weight: 300;
  font-style: italic;
  color: ${getColor('primary.900')};
  margin-top: ${getSpacing('xl')};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
`;

const GoldLine = styled.div`
  width: 80px;
  height: 2px;
  background: #CAB96E;
  margin: ${getSpacing('xl')} auto 0;
  animation: ${drawLine} 1.2s ease-out forwards;
`;



// ─── Section Divider (matching HomePage) ───────────────────────────────────
const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${getSpacing('xl')};

  &::before,
  &::after {
    content: '';
    flex: 1;
    max-width: 120px;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, #CAB96E 50%, transparent 100%);
  }

  & > span {
    color: #CAB96E;
    font-size: 0.5rem;
    margin: 0 ${getSpacing('md')};
    opacity: 0.7;
  }
`;

// ─── Section Title (matching HomePage's StoryHeading pattern) ──────────────
const SectionTitle = styled.h2`
  font-size: 2.85rem;
  font-weight: 500;
  color: #CAB96E;
  margin: 0 0 ${getSpacing('lg')};
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  word-spacing: 0.1em;
  text-align: center;

  @media (max-width: 768px) {
    font-size: clamp(1.6rem, 5vw, 2.4rem);
  }
`;

const SectionSubtitle = styled.p`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 1.1rem;
  font-weight: 300;
  font-style: italic;
  color: ${getColor('primary.800')};
  text-align: center;
  max-width: 650px;
  margin: 0 auto ${getSpacing('3xl')};
  line-height: 1.7;
`;

// ─── Our Story Section ─────────────────────────────────────────────────────
const StorySection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${getSpacing('4xl')};
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${getSpacing('2xl')};
  }
`;

const StoryText = styled.div`
  p {
    font-size: 1rem;
    line-height: 1.7;
    color: ${getColor('primary.800')};
    margin-bottom: ${getSpacing('lg')};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StoryHighlight = styled.div`
  background: linear-gradient(135deg, ${getColor('primary.50')} 0%, ${getColor('neutral.white')} 100%);
  border-left: 3px solid #CAB96E;
  border-radius: 0 ${DesignSystem.borderRadius['2xl']} ${DesignSystem.borderRadius['2xl']} 0;
  padding: ${getSpacing('2xl')} ${getSpacing('xl')};

  blockquote {
    font-family: ${DesignSystem.typography.fonts.display};
    font-size: 1.5rem;
    font-weight: 300;
    font-style: italic;
    color: ${getColor('primary.800')};
    margin: 0 0 ${getSpacing('md')};
    line-height: 1.5;
  }

  cite {
    font-family: ${DesignSystem.typography.fonts.primary};
    font-size: 0.875rem;
    font-style: normal;
    color: #CAB96E;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-weight: 600;
  }
`;

// ─── Timeline Section ──────────────────────────────────────────────────────
const TimelineSection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #CAB96E 0%, ${getColor('primary.200')} 100%);
    transform: translateX(-50%);

    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled.div<{ $align: 'left' | 'right' }>`
  position: relative;
  width: 50%;
  padding: ${getSpacing('xl')} ${getSpacing('2xl')};
  ${props => props.$align === 'left' ? 'margin-right: auto;' : 'margin-left: auto;'}

  @media (max-width: 768px) {
    width: calc(100% - 50px);
    margin-left: 50px;
    padding: ${getSpacing('lg')} 0;
  }
`;

const TimelineDot = styled.div<{ $align: 'left' | 'right' }>`
  position: absolute;
  top: ${getSpacing('xl')};
  ${props => props.$align === 'left' ? 'right: -8px;' : 'left: -8px;'}
  width: 16px;
  height: 16px;
  background: #CAB96E;
  border: 3px solid ${getColor('neutral.white')};
  border-radius: 50%;
  box-shadow: 0 0 0 3px #CAB96E;
  z-index: 2;

  @media (max-width: 768px) {
    left: -42px;
    right: auto;
  }
`;

const TimelineEra = styled.span`
  display: block;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  color: #CAB96E;
  text-transform: uppercase;
  margin-bottom: ${getSpacing('xs')};
`;

const TimelineTitle = styled.h3`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('sm')};
`;

const TimelineDesc = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${getColor('primary.800')};
  margin: 0;
`;

// ─── Meet Our Founder ──────────────────────────────────────────────────────
const FounderSection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const FounderGrid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: ${getSpacing('4xl')};
  align-items: center;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${getSpacing('2xl')};
    text-align: center;
  }
`;

const FounderPhotoWrapper = styled.div`
  position: relative;
  width: 280px;
  height: 280px;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: linear-gradient(135deg, #CAB96E 0%, ${getColor('primary.200')} 100%);
    z-index: 0;
  }

  @media (max-width: 480px) {
    width: 220px;
    height: 220px;
  }
`;

const FounderPhoto = styled.img`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: top;
  z-index: 1;
  border: 4px solid ${getColor('neutral.white')};
`;

const FounderInfo = styled.div`
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const FounderName = styled.h3`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 2rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('xs')};
`;

const FounderRole = styled.span`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #CAB96E;
  text-transform: uppercase;
  margin-bottom: ${getSpacing('xl')};
`;

const FounderBio = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${getColor('primary.800')};
  margin: 0 0 ${getSpacing('xl')};
`;

const FounderBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('sm')};

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FounderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  padding: ${getSpacing('xs')} ${getSpacing('md')};
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.100')};
  border-radius: ${DesignSystem.borderRadius.full};
  font-size: 0.8rem;
  font-weight: 500;
  color: ${getColor('primary.700')};
  white-space: nowrap;

  strong {
    color: #CAB96E;
    font-weight: 700;
  }
`;

// ─── Key Stats Section ─────────────────────────────────────────────────────
const StatsSection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${getSpacing('xl')};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    gap: ${getSpacing('md')};
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: ${getSpacing('2xl')} ${getSpacing('lg')};
  background: linear-gradient(135deg, ${getColor('primary.50')} 0%, ${getColor('neutral.white')} 100%);
  border: 1px solid ${getColor('primary.100')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  transition: all ${DesignSystem.motion.duration.normal} ease;

  &:hover {
    border-color: #CAB96E;
    transform: translateY(-4px);
    box-shadow: ${getShadow('soft')};
  }
`;

const StatNumber = styled.div`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 400;
  color: #CAB96E;
  line-height: 1;
  margin-bottom: ${getSpacing('sm')};
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${getColor('primary.800')};
`;

// ─── What Makes Us Unique ──────────────────────────────────────────────────
const UniqueSection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const UniqueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${getSpacing('2xl')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UniqueCard = styled.div`
  padding: ${getSpacing('2xl')};
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.100')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  transition: all ${DesignSystem.motion.duration.normal} ease;

  &:hover {
    border-color: #CAB96E;
    box-shadow: ${getShadow('soft')};
    transform: translateY(-4px);
  }
`;

const UniqueIcon = styled.div`
  width: 56px;
  height: 56px;
  background: none;
  border: 2px solid #CAB96E;
  border-radius: ${DesignSystem.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: ${getSpacing('lg')};
  display: none;
`;

const UniqueTitle = styled.h3`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('sm')};
`;

const UniqueDesc = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${getColor('primary.800')};
  margin: 0;
`;

// ─── Expertise Section ─────────────────────────────────────────────────────
const ExpertiseSection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const ExpertiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${getSpacing('2xl')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertiseCard = styled.div`
  text-align: center;
  padding: ${getSpacing('2xl')};
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.100')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  transition: all ${DesignSystem.motion.duration.normal} ease;

  &:hover {
    border-color: #CAB96E;
    box-shadow: ${getShadow('soft')};
    transform: translateY(-4px);
  }
`;

const ExpertiseIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${getSpacing('lg')};
  background: none;
  border: 2px solid #CAB96E;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  display: none;
`;

const ExpertiseTitle = styled.h3`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('sm')};
`;

const ExpertiseDesc = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${getColor('primary.800')};
  margin: 0;
`;

// ─── Behind the Lens (Photographer Credits) ────────────────────────────────
const LensSection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const LensGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${getSpacing('lg')};
`;

const LensPhoto = styled.img`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(202, 185, 110, 0.4);
  margin: 0 auto;

  @media (max-width: 480px) {
    width: 140px;
    height: 140px;
  }
`;

const LensName = styled.h3`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('xs')};
`;

const LensRole = styled.span`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #CAB96E;
  text-transform: uppercase;
  margin-bottom: ${getSpacing('md')};
`;

const LensDesc = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${getColor('primary.800')};
  margin: 0 0 ${getSpacing('lg')};
`;

const InstaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  color: #CAB96E;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  padding: ${getSpacing('sm')} ${getSpacing('lg')};
  border: 1px solid rgba(202, 185, 110, 0.4);
  border-radius: ${DesignSystem.borderRadius.full};
  transition: all ${DesignSystem.motion.duration.fast} ease;

  &:hover {
    background: rgba(202, 185, 110, 0.1);
    border-color: #CAB96E;
  }

  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LensPhotographersWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${getSpacing('3xl')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// ─── CTA Section ───────────────────────────────────────────────────────────
const CTASection = styled.section`
  padding: 40px 0;
  background: ${getColor('neutral.white')};
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px 0;
  }
`;

const CTALink = styled.a`
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #CAB96E;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  display: inline-block;
  min-height: 44px;
  line-height: 44px;
  transition: color 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: 8px;
    left: 0;
    width: 0;
    height: 2px;
    background: #CAB96E;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #d4c478;

    &::after {
      width: 100%;
    }
  }

  &:active {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
`;

// ─── Footer (matching HomePage) ────────────────────────────────────────────
const FooterButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  min-height: 44px;
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  transition: color ${DesignSystem.motion.duration.fast} ease;

  &:hover { color: rgba(255, 255, 255, 1); }
  &:active { opacity: 0.7; }
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FooterSocialLink = styled.a`
  color: rgba(255, 255, 255, 0.85);
  transition: color ${DesignSystem.motion.duration.fast} ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('sm')};
  min-height: 44px;
  min-width: 44px;

  &:hover { color: rgba(255, 255, 255, 1); }
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// ─── Component ─────────────────────────────────────────────────────────────
export default function AboutPage({ onNavigateTo }: AboutPageProps) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // ─── Header Dropdown States (centralized pattern) ──────────────────────
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  return (
    <PageContainer>
      <AppHeader
        onNavigateTo={onNavigateTo}
        currentPage="about"
        transparent
        forceScrolled
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* ── 1. Typographic Editorial Hero ─────────────────────────────── */}
      <FadeInContainer>
        <HeroSection>
          <HeroCompanyName>
            Himalayan<br />Rovers
          </HeroCompanyName>
          <HeroEst>Formerly Tibet Trails · Est. 2005 · Leh, Ladakh</HeroEst>
          <HeroSubtitle>
            Two decades of crafting extraordinary Himalayan journeys - from high-altitude treks to curated family expeditions.
          </HeroSubtitle>
        </HeroSection>
      </FadeInContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 2. Our Story ──────────────────────────────────────────────── */}
      <FadeInUpContainer delay={0.15}>
        <StorySection>
          <Container maxWidth="lg">
            <SectionTitle>Our Story</SectionTitle>
            <StoryGrid>
              <StoryText>
                <p>
                  In 2005, when the rugged trails of Ladakh were still whispered about in trekking circles, <strong>Tsewang</strong> founded <strong>Tibet Trails</strong> with a singular vision - to share the raw, untouched beauty of the Trans-Himalayan landscape with the world.
                </p>
                <p>
                  For the first seven years, our clientele was almost entirely international - seasoned trekkers and adventurers from across Europe and the West, drawn to our expertise in Trans-Himalayan expeditions. We built our reputation one summit at a time, one pass at a time.
                </p>
                <p>
                  From 2012, we expanded our horizons. While trekking remains in our DNA, we added Jeep Safari operations and curated family packages, opening the Himalayas to a wider audience - from solo adventurers to families seeking a transformative experience.
                </p>
                <p>
                  In 2023, as our footprint grew to span Ladakh, Kashmir, and Himachal Pradesh, Tibet Trails was reborn as <strong>Himalayan Rovers</strong> - a name that reflects the full breadth of our Himalayan expertise and the spirit of exploration that drives us.
                </p>
              </StoryText>
              <StoryHighlight>
                <blockquote>
                  "The mountains don't judge - they welcome everyone who comes with respect and curiosity."
                </blockquote>
                <cite>Tsewang - Founder</cite>
              </StoryHighlight>
            </StoryGrid>
          </Container>
        </StorySection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 3. Timeline ───────────────────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <TimelineSection>
          <Container maxWidth="lg">
            <SectionTitle>Our Journey</SectionTitle>
            <SectionSubtitle>
              From a small trekking outfit to a full-service Himalayan travel company.
            </SectionSubtitle>
            <TimelineContainer>
              <TimelineItem $align="left">
                <TimelineDot $align="left" />
                <TimelineEra>2005 – 2012</TimelineEra>
                <TimelineTitle>The Pioneer Era</TimelineTitle>
                <TimelineDesc>
                  Specialized exclusively in Trans-Himalayan treks for international adventurers. Built deep expertise across Ladakh's most challenging passes and remote valleys, earning a reputation as one of the region's most trusted trekking outfits.
                </TimelineDesc>
              </TimelineItem>
              <TimelineItem $align="right">
                <TimelineDot $align="right" />
                <TimelineEra>2012 – 2023</TimelineEra>
                <TimelineTitle>The Expansion</TimelineTitle>
                <TimelineDesc>
                  Broadened operations to include Jeep Safari tours and curated family packages across Ladakh, Kashmir, and Himachal Pradesh. Assisted production for wildlife documentaries and films on Buddhism, Ladakh culture, and tradition for several production companies.
                </TimelineDesc>
              </TimelineItem>
              <TimelineItem $align="left">
                <TimelineDot $align="left" />
                <TimelineEra>2023 – Present</TimelineEra>
                <TimelineTitle>A New Identity</TimelineTitle>
                <TimelineDesc>
                  With operations spanning three states and a vision far beyond our origins, Tibet Trails was reborn as Himalayan Rovers - a name that captures the breadth of our expertise and the adventurous spirit of every journey we craft.
                </TimelineDesc>
              </TimelineItem>
            </TimelineContainer>
          </Container>
        </TimelineSection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 4. Meet Our Founder ────────────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <FounderSection>
          <Container maxWidth="lg">
            <SectionTitle>Meet Our Founder</SectionTitle>
            <FounderGrid>
              <FounderPhotoWrapper>
                <FounderPhoto
                  src={`${API_BASE_URL}/api/v1/media/images/Tsewang.webp`}
                  alt="Tsewang - Founder & CEO of Himalayan Rovers"
                  loading="lazy"
                />
              </FounderPhotoWrapper>
              <FounderInfo>
                <FounderName>Tsewang</FounderName>
                <FounderRole>Founder &amp; CEO</FounderRole>
                <FounderBio>
                  A Ladakhi native with an unmatched passion for the mountains, Tsewang founded Tibet Trails in 2005 - now Himalayan Rovers - and has been leading expeditions ever since. His journeys span not just the Himalayas but across India and Europe, including the Tatra Mountain Trek. With over two decades of experience, he brings a rare combination of local wisdom, international exposure, and genuine warmth to every expedition.
                </FounderBio>
                <FounderBadges>
                  <FounderBadge><strong>100+</strong> Treks</FounderBadge>
                  <FounderBadge><strong>20+</strong> Expeditions</FounderBadge>
                  <FounderBadge><strong>40</strong> Treks / Year</FounderBadge>
                  <FounderBadge><strong>5–10</strong> Per Team</FounderBadge>
                  <FounderBadge><strong>India &amp; Europe</strong></FounderBadge>
                </FounderBadges>
              </FounderInfo>
            </FounderGrid>
          </Container>
        </FounderSection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 5. Key Stats ──────────────────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <StatsSection>
          <Container maxWidth="lg">
            <StatsGrid>
              <StatCard>
                <StatNumber>40+</StatNumber>
                <StatLabel>Treks Per Year</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>20+</StatNumber>
                <StatLabel>Years of Excellence</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>5–10</StatNumber>
                <StatLabel>Per Group</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>3</StatNumber>
                <StatLabel>States Covered</StatLabel>
              </StatCard>
            </StatsGrid>
          </Container>
        </StatsSection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 6. What Makes Us Unique ───────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <UniqueSection>
          <Container maxWidth="lg">
            <SectionTitle>What Makes Us Unique</SectionTitle>
            <UniqueGrid>
              <UniqueCard>
                <UniqueIcon></UniqueIcon>
                <UniqueTitle>Eco &amp; Sustainable Tourism</UniqueTitle>
                <UniqueDesc>
                  We believe in leaving only footprints. Our operations prioritize minimal environmental impact, support local communities, and promote responsible travel practices - preserving the fragile Himalayan ecosystem for generations to come.
                </UniqueDesc>
              </UniqueCard>
              <UniqueCard>
                <UniqueIcon></UniqueIcon>
                <UniqueTitle>AI-Powered Personalization</UniqueTitle>
                <UniqueDesc>
                  Cutting-edge AI helps us craft itineraries tailored precisely to your preferences, fitness level, and interests. From route optimization to personalized recommendations, technology enhances every step of your journey.
                </UniqueDesc>
              </UniqueCard>
            </UniqueGrid>
          </Container>
        </UniqueSection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 7. Expertise Highlights ──────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <ExpertiseSection>
          <Container maxWidth="lg">
            <SectionTitle>Our Expertise</SectionTitle>
            <ExpertiseGrid>
              <ExpertiseCard>
                <ExpertiseIcon></ExpertiseIcon>
                <ExpertiseTitle>Trans-Himalayan Treks</ExpertiseTitle>
                <ExpertiseDesc>
                  Our founding strength - expert-led expeditions across Ladakh's most challenging and rewarding high-altitude trails, from Markha Valley to Chadar.
                </ExpertiseDesc>
              </ExpertiseCard>
              <ExpertiseCard>
                <ExpertiseIcon></ExpertiseIcon>
                <ExpertiseTitle>Jeep Safari &amp; Family Packages</ExpertiseTitle>
                <ExpertiseDesc>
                  Comfortable, curated road journeys through Ladakh, Kashmir, and Himachal - designed for families, groups, and those seeking adventure without the altitude.
                </ExpertiseDesc>
              </ExpertiseCard>
              <ExpertiseCard>
                <ExpertiseIcon></ExpertiseIcon>
                <ExpertiseTitle>Film &amp; Documentary Support</ExpertiseTitle>
                <ExpertiseDesc>
                  Production assistance for wildlife films and documentaries on Buddhism, Ladakh's culture, and traditions - trusted by several international production companies.
                </ExpertiseDesc>
              </ExpertiseCard>
            </ExpertiseGrid>
          </Container>
        </ExpertiseSection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 8. Behind the Lens ────────────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <LensSection>
          <Container maxWidth="lg">
            <SectionTitle>Behind the Lens</SectionTitle>
            <SectionSubtitle>
              The stunning visuals across our platform are brought to life by exceptional talent.
            </SectionSubtitle>
            <LensPhotographersWrapper>
              <LensGrid>
                <LensPhoto
                  src={`${API_BASE_URL}/api/v1/media/images/puneet.webp`}
                  alt="Puneet Verma - Lead Photographer"
                  loading="lazy"
                />
                <div>
                  <LensName>Puneet Verma</LensName>
                  <LensRole>Lead Photographer</LensRole>
                  <LensDesc>
                    A breathtaking landscape, a candid moment on the trail, the play of light on ancient monastery walls, Puneet's lens captures the soul of the Himalayas.
                  </LensDesc>
                  <InstaLink
                    href="https://www.instagram.com/thruthatlens/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                      <path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                    </svg>
                    @thruthatlens
                  </InstaLink>
                </div>
              </LensGrid>
              <LensGrid>
                <LensPhoto
                  src={`${API_BASE_URL}/api/v1/media/images/thiru.webp`}
                  alt="Thiru - Lead Photographer"
                  loading="lazy"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div>
                  <LensName>Thiru</LensName>
                  <LensRole>Lead Photographer</LensRole>
                  <LensDesc>
                    A wide-open valley, an unguarded smile on the trail, the quiet stillness between the peaks, Thiru's lens finds the stories hiding in plain sight.
                  </LensDesc>
                  <InstaLink
                    href="https://www.instagram.com/visual_storyteller18/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                      <path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                    </svg>
                    @visual_storyteller18
                  </InstaLink>
                </div>
              </LensGrid>
            </LensPhotographersWrapper>
          </Container>
        </LensSection>
      </FadeInUpContainer>

      <SectionDivider><span>◆</span></SectionDivider>

      {/* ── 9. CTA ────────────────────────────────────────────────────── */}
      <FadeInUpContainer delay={0.2}>
        <CTASection>
          <Container maxWidth="lg">
            <Heading2 style={{
              color: getColor('primary.900'),
              marginBottom: getSpacing('sm'),
              fontSize: '1.5rem',
              fontWeight: 400,
              fontStyle: 'italic',
              letterSpacing: '0.08em',
              lineHeight: 1.25,
              textTransform: 'uppercase' as const,
              fontFamily: "'Poppins', 'Montserrat', 'Futura', sans-serif"
            }}>
              Your Himalayan Adventure Awaits
            </Heading2>
            <BodyLarge style={{
              color: getColor('primary.700'),
              marginBottom: getSpacing('xl'),
              maxWidth: '600px',
              margin: `0 auto ${getSpacing('xl')}`,
              fontStyle: 'italic',
              letterSpacing: '0.08em',
              lineHeight: 1.7,
            }}>
              Two decades of experience. One unforgettable journey. Let's plan yours.
            </BodyLarge>
            <CTALink
              href="#"
              onClick={(e: React.MouseEvent) => { e.preventDefault(); onNavigateTo?.('contact'); }}
            >
              Get in Touch
            </CTALink>
          </Container>
        </CTASection>
      </FadeInUpContainer>

      {/* ── 10. Footer (matching HomePage) ─────────────────────────────── */}
      <footer style={{
        background: '#3D3721',
        color: 'white',
        padding: `${getSpacing('2xl')} 0`,
        textAlign: 'center' as const,
      }}>
        <Container maxWidth="lg">
          <BodyText style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join thousands of travelers who have discovered the magic of the Himalayas with us.
          </BodyText>
          <Spacer size="lg" />
          <Flex justify="center" gap="lg" wrap>
            <FooterButton onClick={() => onNavigateTo?.('home')}>Home</FooterButton>
            <FooterButton onClick={() => onNavigateTo?.('curated-experiences')}>Explore</FooterButton>
            <FooterButton onClick={() => onNavigateTo?.('reviews')}>Reviews</FooterButton>
            <FooterButton onClick={() => onNavigateTo?.('contact')}>Contact</FooterButton>
          </Flex>
          <Spacer size="lg" />
          <Flex justify="center" gap="md" style={{ marginBottom: getSpacing('md') }}>
            <FooterSocialLink href="https://www.instagram.com/himalayan__rovers/" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </FooterSocialLink>
          </Flex>
          <Caption style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
            © 2026 Himalayan Rovers. All rights reserved.
          </Caption>
        </Container>
      </footer>
    </PageContainer>
  );
}
